import { ImageDetect } from "alt1";
import lodash from "lodash";
import { ImageFingerprint } from "../../../lib/util/ImageFingerprint";
import { deps } from "../../dependencies";
import { async_lazy } from "../../../lib/Lazy";
import { Log } from "../../../lib/util/Log";
import { util } from "../../../lib/util/util";
var log = Log.log;
var findBestMatch = util.findBestMatch;
import { FakeLodash } from "../../../lib/coreutil/FakeLodash";
export class SliderReader {
    constructor(references, blanks) {
        this.references = references;
        this.blanks = blanks;
    }
    identify(image, known_theme = undefined) {
        // Parse the slider image from screen
        const tiles = SlideReader.parseSliderImage(image, 7);
        let tile_scores = [];
        const blank_refs = this.blanks;
        const compare_to = known_theme
            ? [this.references[known_theme]]
            : Object.values(this.references);
        for (let tile of tiles.tiles) {
            const best_blank_match = findBestMatch(blank_refs, (blank_ref) => ImageFingerprint.similarity(tile.signature, blank_ref));
            for (let slider of compare_to) {
                for (let ref_tile of slider.tiles) {
                    tile_scores.push({
                        tile: tile,
                        reference_tile: ref_tile,
                        score: ref_tile.position == 24
                            ? best_blank_match.score
                            : ImageFingerprint.similarity(tile.signature, ref_tile.signature)
                    });
                }
            }
        }
        const grouped = lodash.groupBy(tile_scores, e => e.reference_tile.theme);
        const matches = Object.entries(grouped).map(([theme, tile_matches]) => {
            const sorted = FakeLodash.sortBy(tile_matches, e => -e.score);
            const matched_tiles = new Array(25).fill(null);
            const tiles_used = new Array(25).fill(false);
            for (let match of sorted) {
                if (matched_tiles[match.tile.position])
                    continue;
                if (tiles_used[match.reference_tile.position])
                    continue;
                if (SlideReader.DEBUG_SLIDE_READER) {
                    console.log(`Matching ${match.tile.position} to reference tile #${match.reference_tile.position}, score ${match.score.toFixed(3)}`);
                    console.log(match.tile.signature);
                    console.log(match.reference_tile.signature);
                }
                matched_tiles[match.tile.position] = match;
                tiles_used[match.reference_tile.position] = true;
            }
            if (SlideReader.DEBUG_SLIDE_READER)
                console.log(`Matched ${theme}, total score ${FakeLodash.sumBy(matched_tiles, t => t.score).toFixed(3)}`);
            if (SlideReader.DEBUG_SLIDE_READER) {
                const debug_for = [];
                for (let ref_tile of debug_for) {
                    console.log(`Similarity to reference tile ${ref_tile}`);
                    const tiles = FakeLodash.sortBy(tile_matches.filter(m => m.reference_tile.position == ref_tile), e => e.tile.position);
                    for (let y = 0; y < 5; y++) {
                        console.log(tiles.slice(y * 5, (y + 1) * 5).map(t => t.score.toFixed(2)).join(", "));
                    }
                    console.log();
                }
                console.log("Chosen similarity");
                for (let y = 0; y < 5; y++) {
                    console.log(matched_tiles.slice(y * 5, (y + 1) * 5).map(t => t.score.toFixed(2)).join(", "));
                }
            }
            return {
                theme: theme,
                all_match_pairs: tile_matches,
                preliminary_best_matching: {
                    score: FakeLodash.sumBy(matched_tiles, e => e.score) / matched_tiles.length,
                    match: matched_tiles
                }
            };
        });
        const greedy_best = FakeLodash.maxBy(matches, m => m.preliminary_best_matching.score);
        if (SlideReader.DEBUG_SLIDE_READER) {
            const perfect_pairs = greedy_best.all_match_pairs.filter(e => e.score == 1);
            console.log(`Perfect score pairs ${perfect_pairs.length}`);
            perfect_pairs.forEach(e => {
                console.log((`Perfect Match: Tile ${e.tile.position} with ${e.reference_tile.position}`));
            });
        }
        const DO_BACKTRACKING_IMPROVEMENT = deps().app.settings.settings.solving.puzzles.sliders.improve_slider_matches_backtracking
            && greedy_best.preliminary_best_matching.score > SlideReader.DETECTION_THRESHOLD_SCORE;
        if (DO_BACKTRACKING_IMPROVEMENT) {
            let best = greedy_best.preliminary_best_matching.match;
            let best_score = greedy_best.preliminary_best_matching.score * 25;
            const working_tiles = new Array(25).fill(null);
            const max_improvements = new Array(25).fill(0).map((_, i) => {
                return Math.max(...greedy_best.all_match_pairs.slice(i * 25, (i + 1) * 25).map(e => e.score));
            });
            for (let i = 23; i >= 0; i--) {
                max_improvements[i] += max_improvements[i + 1];
            }
            const reference_used = new Array(25).fill(false);
            let seen = 0;
            function backtracking(i, similarity_so_far) {
                seen++;
                if (seen > 10000)
                    return;
                if (i >= 25) {
                    if (similarity_so_far > best_score) {
                        log().log(`Backtracking found a better match from ${best_score} to ${similarity_so_far}`, "Slide Reader");
                        best = [...working_tiles];
                        best_score = similarity_so_far;
                    }
                    return;
                }
                for (let index = 0; index < 25; index++) {
                    const match = greedy_best.all_match_pairs[i * 25 + index];
                    if (reference_used[match.reference_tile.position] || match.score < SlideReader.DETECTION_THRESHOLD_SCORE || (similarity_so_far + match.score) + max_improvements[i + 1] <= best_score) {
                        continue;
                    }
                    working_tiles[i] = match;
                    reference_used[match.reference_tile.position] = true;
                    backtracking(i + 1, similarity_so_far + match.score);
                    reference_used[match.reference_tile.position] = false;
                }
            }
            if (SlideReader.DEBUG_SLIDE_READER)
                console.log(`Potential ${max_improvements[0]} vs. ${best_score}`);
            const better_match_max_exist = max_improvements[0] > best_score;
            if (better_match_max_exist) {
                backtracking(0, 0);
            }
            if (SlideReader.DEBUG_SLIDE_READER)
                console.log(`Before BT: ${greedy_best.preliminary_best_matching.score}, After BT: ${best_score / 25}`);
            return {
                tiles: best.map(m => m.reference_tile),
                theme: greedy_best.theme,
                match_score: best_score / 25
            };
        }
        else {
            return {
                tiles: greedy_best.preliminary_best_matching?.match.map(e => e.reference_tile),
                theme: greedy_best.theme,
                match_score: greedy_best.preliminary_best_matching.score
            };
        }
    }
}
export var SlideReader;
(function (SlideReader) {
    const _instance = async_lazy(async () => new SliderReader(await reference_sliders.get(), await SlideReader._blank_tile_reference.get()));
    async function instance() {
        return _instance.get();
    }
    SlideReader.instance = instance;
    SlideReader.DETECTION_THRESHOLD_SCORE = 0.6;
    SlideReader.DEBUG_SLIDE_READER = false;
    SlideReader.SLIDER_SIZE = 5;
    SlideReader.KERNEL_INTERVAL = 7;
    SlideReader.TILE_SIZE = 49;
    function parseTile(img, offset) {
        return ImageFingerprint.get(img, offset, { x: SlideReader.TILE_SIZE, y: SlideReader.TILE_SIZE }, { x: SlideReader.KERNEL_INTERVAL, y: SlideReader.KERNEL_INTERVAL });
    }
    SlideReader.parseTile = parseTile;
    /**
     * Parses the tiles of a slider image from a reference image.
     * Tiles are assumed to be 49 by 49 pixels.
     *
     * @param img The image to parse.
     * @param gutter The size of the gap between pixels. For clean reference images this is 0, for captures images from Alt1 this should be 7.
     * @param known_theme The theme of the slider if known
     */
    function parseSliderImage(img, gutter = 0, known_theme = undefined) {
        // Assumes a slider size of 245 x 245!
        const tiles = [];
        for (let y = 0; y < SlideReader.SLIDER_SIZE; y++) {
            for (let x = 0; x < SlideReader.SLIDER_SIZE; x++) {
                tiles.push({
                    theme: known_theme,
                    position: y * SlideReader.SLIDER_SIZE + x,
                    signature: parseTile(img, { x: x * (SlideReader.TILE_SIZE + gutter), y: y * (SlideReader.TILE_SIZE + gutter) })
                });
            }
        }
        return { tiles, theme: known_theme };
    }
    SlideReader.parseSliderImage = parseSliderImage;
    SlideReader.reference_slider_themes = ["adventurer", "araxxor", "archers", "black_dragon", "bridge", "castle", "clan_citadel", "corporeal_beast", "drakan_bloodveld",
        "elves", "general_graardor", "gregorovic", "helwyr", "ice_strykewyrm", "menaphos_pharaoh", "nomad", "nymora", "seal", "sword_of_edicts", "tree", "troll", "tuska", "v", "vanstrom_klause", "werewolf", "wizard", "wyvern"
    ];
    async function downloadReferenceSliderFromTheme(theme) {
        return parseSliderImage(await ImageDetect.imageDataFromUrl(getThemeImageUrl(theme)), 0, theme);
    }
    SlideReader.downloadReferenceSliderFromTheme = downloadReferenceSliderFromTheme;
    let SimplifiedReferenceSliderMap;
    (function (SimplifiedReferenceSliderMap) {
        function encode(sliders) {
            return Object.fromEntries(sliders.map(slider => {
                return [slider.theme, slider.tiles.map(t => ImageFingerprint.toBase64(t.signature))];
            }));
        }
        SimplifiedReferenceSliderMap.encode = encode;
        function decode(self) {
            return lodash.mapValues(self, (tiles, theme) => {
                return {
                    tiles: tiles.map((t, i) => ({ position: i, signature: ImageFingerprint.fromBase64(t), theme: theme })),
                    theme: theme
                };
            });
        }
        SimplifiedReferenceSliderMap.decode = decode;
    })(SimplifiedReferenceSliderMap = SlideReader.SimplifiedReferenceSliderMap || (SlideReader.SimplifiedReferenceSliderMap = {}));
    const reference_sliders = async_lazy(async () => {
        const res = await fetch("/data/sliders/slider_references.json");
        return SimplifiedReferenceSliderMap.decode((await res.json()));
    });
    SlideReader._blank_tile_reference = async_lazy(async () => {
        const data = await ImageDetect.imageDataFromUrl("/data/sliders/sliders/blanktiles.png");
        const tile_number = data.width / SlideReader.TILE_SIZE;
        const variants = [];
        for (let i = 0; i < tile_number; i++) {
            variants.push(parseTile(data, { x: i * SlideReader.TILE_SIZE, y: 0 }));
        }
        return variants;
    });
    function getThemeImageUrl(theme) {
        return `data/sliders/sliders/${theme}.png`;
    }
    SlideReader.getThemeImageUrl = getThemeImageUrl;
})(SlideReader || (SlideReader = {}));
